/**
 * Anlagemaske - 1:1-Portierung des Anlage-Dialogs (`index.html:3761-3812`, `saveNewOrder`
 * `index.html:8090-8158`), MIT den beiden benannten Verhaltensaenderungen (Spec, "Bewusste
 * Verhaltensaenderungen", Nr. 3 und 4):
 *
 * 4. Kein Bestellnummernfeld - JTL vergibt die Nummer, die Bestellung zeigt bis dahin
 *    "Nummer wird vergeben". ACHTUNG: der Bestand hat HEUTE SCHON kein Bestellnummernfeld in
 *    diesem Dialog (`index.html:3771-3792` listet Lieferant/Lager/Liefertermin/vsl. LT
 *    Buchen/Rechnungsnummer - keine Bestellnummer) und `blankOrder()` setzt `orderNo: ''` bereits
 *    beim Anlegen (`index.html:7846`). Der Text "Nummer wird vergeben" selbst kommt NICHT aus
 *    `index.html` (dort gibt es ihn nirgends, siehe Taskbericht) - er ist woertlich aus der Spec
 *    uebernommen (`docs/superpowers/specs/2026-08-04-frontend-react-rewrite-design.md:226`).
 * 3. Lieferant UND Artikel kommen ueber `JtlSuchfeld`/`PositionsEditor` (JTL-Suche) statt aus dem
 *    heutigen `<select id="newOrderSupplier">` (gespeist aus dem LOKALEN, selbst gepflegten
 *    `DB.suppliers`, `index.html:5017`) bzw. den frei eingetippten Artikel-/SKU-Feldern
 *    (`index.html:7998-7999`). Der Bestellexcel-Import (`PositionsEditor.tsx`) FUELLT die Zeilen nur
 *    vor - jede Zeile braucht danach trotzdem einen Treffer aus der JTL-Suche (siehe `absenden` unten,
 *    "ohneArtikel"), genau wie eine von Hand angelegte.
 *
 * Diese Flaeche ist bewusst NICHT gegen die Screenshot-Baseline abgesichert
 * (`test/visual/ausnahmen.ts`, Eintrag `NewOrder/Anlagemaske`) - der Nachweis laeuft ausschliesslich
 * ueber `test/komponenten/NewOrder/anlagemaske.test.tsx`.
 *
 * ================= Validierung: submit-seitig, wie der Bestand =================
 *
 * `saveNewOrder` prueft ZWEI Ebenen (index.html:8094/8100-8103): zuerst `form.reportValidity()`
 * (natives HTML5 `required` auf Lager/Liefertermin/vsl. LT Buchen - hier UNVERAENDERT als
 * `required`-Attribute uebernommen), danach eine eigene Meldung im Fehlertext
 * `#newOrderError[role=alert]`. Der Lieferant hat im Bestand KEIN natives `required` (er ist ein
 * `<select>`, dessen erste, leere Option das Formular ohnehin durchliesse) - die Pruefung "kommt der
 * Lieferant wirklich aus der Auswahl" ist hier NEU (Verhaltensaenderung 3 verlangt sie: ein frei
 * eingetippter Name, der zu keinem Treffer gehoert, darf nicht durchgehen) und laeuft deshalb
 * ausschliesslich ueber denselben Fehlertext-Kanal, nicht ueber `required`.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent, type JSX } from "react";
import { hatRecht } from "../../state/berechtigungen.ts";
import { lagerAuswahl, vorgabeLagerName, type JtlLager } from "../../lib/lager.ts";
import { ABSINA_BILLING_ADDRESS } from "../../config/warehouses.ts";
import { Ikone } from "../Ikone.tsx";
import { aktivMarke } from "../Suppliers/AktivMarke.tsx";
import { JtlSuchfeld, type JtlTreffer } from "./JtlSuchfeld.tsx";
import { PositionsEditor, type JtlArtikelTreffer } from "./PositionsEditor.tsx";
import type { Order, OrderItem } from "../../lib/types.ts";
import type { ExcelEntwurf } from "../../lib/parsers/naht.ts";

export interface JtlLieferantTreffer extends JtlTreffer {
  name?: unknown;
  eur?: boolean;
  /**
   * `JtlAktiv` ("Y" | "N" | null) - seit dem 2026-08-13 Teil der Suchantwort. ROH, nicht als
   * Wahrheitswert: `null` heisst unbekannt und darf nicht als "aktiv" durchgehen. Eingeordnet wird
   * in `AktivMarke` (`lib/jtlAktiv.ts`), an EINER Stelle fuer beide Lieferantensuchen.
   *
   * Optional, weil die Prop `lieferantenSuche` von aussen kommt: ein Aufrufer, der den Zustand
   * nicht kennt, bekommt dann dieselbe Marke wie ein unbekannter Zustand - und nicht etwa keine.
   */
  aktiv?: unknown;
}

/** Der an `onSpeichern` uebergebene Entwurf - die vom Formular erfassten Felder, keine fertige `Order`. */
export interface AnlagemaskeDaten {
  supplierId: string;
  supplierName: string;
  eur: boolean;
  warehouse: string;
  /** Nur fuer eine unveraendert vorbefuellte Bestellkopie: die JTL-Kennung eines inaktiven Lagers. */
  duplikatLagerId?: number;
  deliveryTermText: string;
  estimatedDeliveryBuchen: string;
  invoiceNo: string;
  billingAddress: typeof ABSINA_BILLING_ADDRESS;
  items: OrderItem[];
  /**
   * Die gewaehlte Bestellexcel-Datei, `null` ohne Auswahl - `PositionsEditorProps.onExcelDatei`
   * reicht sie hierher. `neueBestellungAnlegen` (`App.tsx`) legt sie NACH dem Anlegen als Beleg ab
   * (`dokumentHochladen(orderId, "bestellexcel", ...)`, Teil 2b des Task-Briefs).
   */
  excelDatei: File | null;
  /**
   * Der volle Auswertungsentwurf zur gewaehlten Bestellexcel-Datei, `null` ohne Auswahl - derselbe
   * Rueckruf (`PositionsEditorProps.onExcelDatei`) reicht ihn zusammen mit der Datei hierher.
   * `neueBestellungAnlegen` (`App.tsx`) ergaenzt ihn NACH dem erfolgreichen Beleg-Upload um die
   * tatsaechlich uebernommenen Positionen und `appliedAt` und schreibt ihn als `orderExcelImport`
   * (Task 4, Plan 2026-08-17-bestellexcel-anlagemaske; `feldgruppen.ts`, Gruppe `extras`).
   */
  excelEntwurf: ExcelEntwurf | null;
}

/** Die drei fachlich erlaubten Uebernahmen einer Bestellkopie. */
export interface AnlagemaskeDuplikat {
  lieferant: JtlLieferantTreffer;
  lager: string;
  lagerId?: number;
  positionen: OrderItem[];
}

export interface AnlagemaskeProps {
  bestellungen: readonly Order[];
  /**
   * Die aktiven JTL-Warenlager (`GET /api/jtl/warehouses`) - Grundlage der Lager-Auswahl
   * (`lib/lager.ts#lagerAuswahl`). VERPFLICHTEND, nicht optional: `App.tsx` ist der einzige
   * Aufrufer und liefert immer ein (anfangs leeres) Array - eine optionale Prop wuerde einem
   * kuenftigen Aufrufer erlauben, sie STILL wegzulassen und die Vorbelegung/Auswahl damit
   * unbemerkt zu verlieren. Darf leer sein, solange der Abruf in `App.tsx` noch laeuft oder
   * fehlgeschlagen ist: die Auswahl bleibt dann leer, `required` greift wie gehabt.
   */
  jtlLager: readonly JtlLager[];
  lieferantenSuche: (begriff: string) => Promise<JtlLieferantTreffer[]>;
  /**
   * Zweiter, optionaler Parameter `kontext` seit Auftrag "Verknuepfungsstatus in der Anlagemaske"
   * (2026-08-28) - `App.tsx#anlagemaskeArtikelSuche` erwartet hier die gewaehlte
   * Lieferanten-Kennung (`kLieferant`), damit die Trefferliste den Verknuepfungsstatus DES
   * gewaehlten Lieferanten zeigt. Diese Komponente reicht ihn selbst ein (`positionsArtikelSuche`
   * unten) - `PositionsEditor`s eigene Prop bleibt bei EINEM Parameter.
   */
  artikelSuche: (begriff: string, kontext?: string) => Promise<JtlArtikelTreffer[]>;
  /**
   * DIESELBE Suche wie `artikelSuche`, aber OHNE die Debounce-Drossel `jtlSuche(...)` davor - Fund
   * beim Bugreport "JTL-Autoabgleich verknuepft nur einen Bruchteil der SKUs" (2026-09-04):
   * `PositionsEditor.tsx#excelDateiVerarbeiten` ruft bis zu `MAX_GLEICHZEITIGE_SUCHEN = 4`
   * gleichzeitige, VERSCHIEDENE SKUs ab - `artikelSuche` ist aber selbst schon eine gedrosselte
   * `jtlSuche(...)`-Instanz mit EINEM geteilten, modulweiten Debounce-Timer (`api/jtlSuche.ts`,
   * Kopfkommentar dort: "je Suchfeld ... wird jtlSuche(...) genau EINMAL aufgerufen") - mehrere
   * gleichzeitige, verschiedene Suchbegriffe kollidieren dort im selben `wartende`-Array, und nur der
   * ZULETZT eingereihte wird tatsaechlich abgefragt; alle anderen Aufrufer bekommen faelschlich genau
   * dessen Ergebnis. `JtlAutoLink.ts` hat mit `begrenztAusfuehren` bereits eine EIGENE
   * Nebenlaeufigkeitsgrenze - diese rohe Prop ist deshalb der richtige Einhaengepunkt fuer den
   * Excel-Import, waehrend `artikelSuche` (gedrosselt) weiterhin dem interaktiven Tippfeld dient.
   */
  artikelSucheRoh: (begriff: string, kontext?: string) => Promise<JtlArtikelTreffer[]>;
  onSpeichern: (daten: AnlagemaskeDaten) => void;
  onSchliessen: () => void;
  speichernLaeuft?: boolean;
  rechte: ReadonlySet<string>;
  /**
   * Vorbelegung aus einer Bestellung. Aus der Quelle wird kein Zahlungsplan kopiert und keine sonstigen
   * Hub-Daten; das Standard-Zahlungsziel des Lieferanten greift wie bei jeder Neuanlage.
   */
  duplikat?: AnlagemaskeDuplikat;
}

export function Anlagemaske({
  bestellungen,
  jtlLager,
  lieferantenSuche,
  artikelSuche,
  artikelSucheRoh,
  onSpeichern,
  onSchliessen,
  speichernLaeuft = false,
  rechte,
  duplikat,
}: AnlagemaskeProps): JSX.Element {
  const [lieferantText, setLieferantText] = useState(() => String(duplikat?.lieferant.name ?? ""));
  const [lieferant, setLieferant] = useState<JtlLieferantTreffer | null>(duplikat?.lieferant ?? null);
  const [lager, setLager] = useState(() => duplikat?.lager ?? "");
  const [duplikatLagerId, setDuplikatLagerId] = useState(() => duplikat?.lagerId);
  /**
   * `onAuswahl` von `JtlSuchfeld` fuer den Lieferanten (Zeile ~300) - setzt NICHT nur `lieferant`,
   * sondern raeumt zugleich den `verknuepft`-Stand jeder bereits uebernommenen Position ab
   * (Pruefungsbefund "Wichtig" zur Verknuepfungsanzeige, 2026-09-01): dieser Stand galt fuer den
   * VORHERIGEN Lieferanten und wird sonst STILL falsch weitergezeigt - eine gruen gewaehlte Position
   * bliebe nach dem Wechsel gruen, obwohl der neue Lieferant sie gar nicht fuehrt (und umgekehrt eine
   * rote faelschlich rot, wenn der neue sie sehr wohl fuehrt). `null` ("unbekannt"), nicht geraten -
   * eine erneute Suche/Uebernahme oder der Speichern-Riegel liefert den echten Stand zum NEUEN
   * Lieferanten. Betrifft nur die ANZEIGE: das Speichern selbst prueft ohnehin jede Position frisch
   * gegen den dann geltenden Lieferanten (`pruefeVerknuepfungen`, Backend).
   */
  function lieferantWaehlen(treffer: JtlLieferantTreffer | null): void {
    setLieferant(treffer);
    setPositionen((alt) =>
      alt.some((p) => "verknuepft" in p)
        ? alt.map((p) => ("verknuepft" in p ? { ...p, verknuepft: null } : p))
        : alt,
    );
  }
  /**
   * Haelt fest, ob der NUTZER das Lager schon selbst gewaehlt hat - der Wächter fuer die
   * Vorbelegung unten. ALS `useRef`, NICHT als State: der Vorbelegungs-Effekt kann bereits
   * eingeplant sein, wenn der Nutzer waehlt - seine Closure haelt dann noch den Waechter-Stand VOM
   * RENDER, in dem er eingeplant wurde. Ein `setState` im `onChange` aendert daran nichts: der
   * laengst eingeplante Effekt liest beim Ausfuehren weiterhin `false` aus SEINER Closure und reiht
   * sein `setLager(vorgabe)` HINTER dem `setLager(event.target.value)` des Nutzers ein - und
   * gewinnt. `ref.current` dagegen wird beim Ausfuehren des Effekts IMMER frisch gelesen, egal wann
   * der Effekt eingeplant wurde; im `onChange` synchron gesetzt (noch VOR `setLager`), ist der neue
   * Stand fuer jeden folgenden Effektlauf sofort sichtbar - keine Closure haelt einen alten Wert
   * fest. Empirisch beobachtet und per Mutationsprobe bestaetigt (Taskbericht Auftrag 4: State
   * statt Ref zurueckgebaut -> derselbe Ablauf wie im Betrieb schlug in der Suite tatsaechlich fehl,
   * `expect(lager.value).toBe("Amazon FBA")` erhielt `"WMS-Lager"`): "Neue Bestellung" schickte
   * `lagerId: 2` (Vorgabe) statt der vom Nutzer gewaehlten `3`.
   */
  const lagerVomNutzerGewaehltRef = useRef(Boolean(duplikat?.lager));
  const [liefertermin, setLiefertermin] = useState("");
  const [vslLtBuchen, setVslLtBuchen] = useState("");
  const [rechnungsnummer, setRechnungsnummer] = useState("");
  const [positionen, setPositionen] = useState<OrderItem[]>(() => duplikat?.positionen ?? []);
  const [excelDatei, setExcelDatei] = useState<File | null>(null);
  // Zusammen mit `excelDatei` gesetzt (derselbe Rueckruf, `PositionsEditorProps.onExcelDatei`) - siehe
  // Kopfkommentar von `AnlagemaskeDaten#excelEntwurf`.
  const [excelEntwurf, setExcelEntwurf] = useState<ExcelEntwurf | null>(null);
  /**
   * Meldet `PositionsEditorProps.onExcelLadenAendern` - der busy-Riegel aus `saveNewOrder`
   * (`index.html:8104-8107`), bis `a434ea7` NICHT portiert (Pruefungsbefund, wichtiger Befund 1):
   * ohne ihn kann `absenden` waehrend des SheetJS-Ladens durchlaufen und legt dann die ALTEN
   * Positionen ohne Beleg an - loest der Parser danach auf, laufen `onAendern`/`onExcelDatei` in
   * die bereits geschlossene Maske und verpuffen.
   */
  const [excelBusy, setExcelBusy] = useState(false);
  const [fehler, setFehler] = useState("");

  const darfAnlegen = hatRecht(rechte, "edit_orders");

  /**
   * Vorgabe WMS-Lager (`lib/lager.ts#VORGABE_LAGER_KENNUNG`), sobald die JTL-Liste da ist - aber
   * NUR, solange der Nutzer noch nicht selbst gewaehlt hat. Faehrt JTL die Kennung 2 nicht,
   * liefert `vorgabeLagerName` `""` und das Feld bleibt leer; `required` greift dann wie bisher.
   */
  useEffect(() => {
    if (lagerVomNutzerGewaehltRef.current) return;
    const vorgabe = vorgabeLagerName(jtlLager);
    if (vorgabe) setLager(vorgabe);
  }, [jtlLager]);

  /**
   * Die Bestellwaehrung folgt dem Lieferanten (`tlieferant.cWaehrungISO`, im Suchtreffer als
   * `eur`) - WOERTLICH dieselbe Bedingung, mit der `App.tsx` (`waehrung: daten.eur ? "EUR" : "USD"`)
   * den Beleg anlegt und mit der `orderCurrency` (`lib/suppliers.ts`) ihn spaeter im Board
   * darstellt. Bis zum 2026-08-16 rechnete die Maske selbst gar nicht mit ihr: der Warenwert stand
   * fest in Euro, waehrend der Beleg daneben in USD entstand - bei 24 der 50 Lieferanten falsch.
   *
   * Solange kein Lieferant gewaehlt ist, gilt dasselbe wie in `orderCurrency`: kein Lieferant,
   * also kein `eur`, also USD. Die Frage stellt sich in der Maske kaum - der Lieferant steht ganz
   * oben und ist vor der ersten Position gewaehlt -, und eine eigene dritte Regel dafuer waere
   * genau die Sorte stiller Abweichung, die diesen Fehler ausgemacht hat.
   */
  const waehrung = lieferant?.eur ? "EUR" : "USD";

  /**
   * Baut die aktuelle Lieferanten-Kennung fest in `artikelSuche` ein - `PositionsEditor`s eigene
   * Prop-Signatur bleibt UNVERAENDERT (`(begriff: string) => Promise<JtlArtikelTreffer[]>`), sie
   * kennt den Lieferanten nicht. Der `useMemo`-Dep auf `lieferant?.id` sorgt zusaetzlich dafuer,
   * dass `PositionsEditor`s EIGENER `gedrosselt = useMemo(() => jtlSuche({abruf: artikelSuche}),
   * [artikelSuche])` (PositionsEditor.tsx, eigene, unveraenderte `jtlSuche`-Instanz mit EIGENEM
   * Cache) bei jedem Lieferantenwechsel neu aufgebaut wird - doppelte Absicherung gegen ein
   * Cross-Lieferant-Leck, zusaetzlich zum korrekt geschluesselten Cache in `api/jtlSuche.ts`.
   */
  const positionsArtikelSuche = useMemo(
    () => (begriff: string) => artikelSuche(begriff, lieferant?.id ?? undefined),
    [artikelSuche, lieferant?.id],
  );

  /**
   * DIESELBE Bauform wie `positionsArtikelSuche` oben (Lieferanten-Kennung fest einbauen), aber um
   * die ROHE `artikelSucheRoh` herum - OHNE ueber `jtlSuche(...)` zu laufen. Einziger Verbraucher:
   * `PositionsEditor.tsx#excelDateiVerarbeiten` (siehe Kopfkommentar der `artikelSucheRoh`-Prop
   * oben, "JTL-Autoabgleich verknuepft nur einen Bruchteil der SKUs") - das interaktive Tippfeld
   * bleibt bei `positionsArtikelSuche` (gedrosselt).
   */
  const positionsArtikelSucheRoh = useMemo(
    () => (begriff: string) => artikelSucheRoh(begriff, lieferant?.id ?? undefined),
    [artikelSucheRoh, lieferant?.id],
  );

  function absenden(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!darfAnlegen || speichernLaeuft) return;
    if (!event.currentTarget.reportValidity()) return;
    // `index.html:8104-8107`, woertlich: waehrend die Bestellexcel noch ausgelesen wird, darf nicht
    // gespeichert werden - sonst gingen die ALTEN Positionen (bzw. keine) und kein Beleg heraus.
    if (excelBusy) {
      setFehler("Bitte warten, bis die Bestellexcel vollständig ausgelesen wurde.");
      return;
    }
    if (!lieferant) {
      setFehler("Bitte einen Lieferanten aus der JTL-Suche auswählen.");
      return;
    }
    /**
     * SEIT DEM 2026-08-15, mit der Angleichung des Artikelbereichs: „Artikel hinzufügen" kann eine
     * Zeile OHNE `kArtikel` erzeugen, und bis hierher stand bei leerer Liste ERSATZWEISE eine leere
     * Position im Entwurf (`items: positionen.length ? positionen : [{...}]`). Beides wiese
     * `pruefeNeueBestellung` (`backend/src/jtl/schreiben/anlegen.ts:86-96`) zurueck - die leere
     * Liste als „Bestellung ohne Positionen", die artikellose Zeile als FREIPOSITION. Die Pruefung
     * hier ist der SPIEGEL jener beiden Riegel, damit der Fehlschlag am Feld steht statt am Backend.
     */
    if (positionen.length === 0) {
      setFehler("Bitte mindestens einen Artikel erfassen.");
      return;
    }
    const ohneArtikel = positionen.findIndex((position) => {
      const kArtikel = Number((position as { kArtikel?: unknown }).kArtikel);
      return !Number.isInteger(kArtikel) || kArtikel < 1;
    });
    if (ohneArtikel >= 0) {
      setFehler(
        `Position ${ohneArtikel + 1}: bitte einen Artikel aus der JTL-Suche wählen.`,
      );
      return;
    }
    setFehler("");
    onSpeichern({
      supplierId: lieferant.id,
      supplierName: String(lieferant.name ?? ""),
      eur: Boolean(lieferant.eur),
      warehouse: lager,
      duplikatLagerId,
      deliveryTermText: liefertermin,
      estimatedDeliveryBuchen: vslLtBuchen,
      invoiceNo: rechnungsnummer.trim(),
      billingAddress: ABSINA_BILLING_ADDRESS,
      // Ohne Ersatzposition - die Pruefung oben laesst eine leere Liste gar nicht mehr hierher.
      items: positionen,
      excelDatei,
      excelEntwurf,
    });
  }

  return (
    <section
      className="board-editor-dialog new-order-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="newOrderTitle"
    >
      <div className="board-editor-head">
        <div className="board-editor-head-copy">
          <h2 id="newOrderTitle">Neue Bestellung vorbereiten</h2>
          <p>
            Lieferant, Lager, Liefertermin und den voraussichtlichen Liefertermin für 74722 Buchen
            festlegen.
          </p>
        </div>
        {/* "Vorschau schließen" ist woertlich aus index.html:3767 uebernommen - der Titel des
            Dialogs selbst spricht von "Bestellung", der Knopf-Titel von "Vorschau"; eine
            Auffaelligkeit des Bestands, nicht repariert (siehe Taskbericht). */}
        <button
          className="icon-btn"
          type="button"
          onClick={onSchliessen}
          title="Vorschau schließen"
          disabled={speichernLaeuft}
        >
          <Ikone name="x" />
        </button>
      </div>
      <form className="new-order-form" onSubmit={absenden}>
        <div className="new-order-body">
          <span className="new-order-ordernumber-hint">Nummer wird vergeben</span>
          <div className="new-order-grid">
            <label className="new-order-field">
              <span>Lieferant</span>
              <JtlSuchfeld<JtlLieferantTreffer>
                id="newOrderSupplier"
                label="Lieferant"
                suche={lieferantenSuche}
                beschriftung={(treffer) => String(treffer.name ?? "")}
                // Seit dem 2026-08-13: ein stillgelegter Lieferant ist SICHTBAR stillgelegt. Hier
                // zaehlt die Angabe mehr als auf der Lieferantenflaeche - dort schaut man einen
                // Lieferanten nach, hier waehlt man ihn fuer eine echte Bestellung aus. Gesperrt
                // wird er trotzdem nicht: es gibt Gruende, bei einem stillgelegten Lieferanten
                // nachzubestellen, und eine Sperre ohne Ausweg waere schlimmer als ein Hinweis.
                // AUFRUF, nicht `<AktivMarke/>`: nur so kommt bei einem aktiven Lieferanten wirklich
                // `null` an, und `JtlSuchfeld` laesst die Randspalte dann ganz weg statt eine leere
                // Huelle zu zeichnen (siehe Kopfkommentar von `AktivMarke.tsx`).
                zusatz={(treffer) => aktivMarke(treffer.aktiv)}
                wert={lieferantText}
                onWertAendern={setLieferantText}
                onAuswahl={lieferantWaehlen}
                platzhalter="Lieferant suchen"
              />
            </label>
            <label className="new-order-field">
              <span>Lager</span>
              <select
                id="newOrderWarehouse"
                required
                aria-required="true"
                value={lager}
                onChange={(event) => {
                  const wert = event.target.value;
                  // NUR eine NICHTLEERE Auswahl zaehlt als Nutzerwahl (G3): ein <select> ohne
                  // passende Option faellt nativ auf "" zurueck - waehlt der Nutzer den Leereintrag,
                  // bevor die JTL-Liste da ist (oder faellt die Auswahl aus anderem Grund darauf
                  // zurueck), verstummte die Vorbelegung sonst FUER IMMER, obwohl der Nutzer gar
                  // nichts Sinnvolles gewaehlt hat.
                  //
                  // GRENZE DIESES SCHUTZES (Pruefung von `3dd7480`): war das Feld nur von der
                  // VORBELEGUNG gefuellt und der Nutzer leert es danach bewusst, bleibt die Ref
                  // `false`. Dass die Vorbelegung dann nicht erneut zuschlaegt, haengt allein an
                  // der Abhaengigkeitsliste des Effekts (`[jtlLager]` - die Liste wird je Anmeldung
                  // genau einmal geladen), NICHT am Waechter. Wer einen Wiederholversuch oder ein
                  // Nachladen der Lagerliste nachruestet, muss das hier mitdenken.
                  if (wert) lagerVomNutzerGewaehltRef.current = true;
                  setDuplikatLagerId(wert === duplikat?.lager ? duplikat?.lagerId : undefined);
                  setLager(wert);
                }}
              >
                <option value=""></option>
                {(lagerAuswahl(bestellungen, jtlLager).includes(duplikat?.lager ?? "")
                  ? lagerAuswahl(bestellungen, jtlLager)
                  : [duplikat?.lager ?? "", ...lagerAuswahl(bestellungen, jtlLager)].filter(Boolean)
                ).map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            <label className="new-order-field">
              <span>Liefertermin</span>
              <input
                id="newOrderDeliveryTerm"
                required
                aria-required="true"
                type="text"
                autoComplete="off"
                placeholder="z. B. KW 36 oder schnellstmöglich"
                value={liefertermin}
                onChange={(event) => setLiefertermin(event.target.value)}
              />
            </label>
            <label className="new-order-field">
              <span>vsl. LT 74722 Buchen</span>
              <input
                id="newOrderEstimatedDeliveryBuchen"
                required
                aria-required="true"
                type="date"
                value={vslLtBuchen}
                onChange={(event) => setVslLtBuchen(event.target.value)}
              />
            </label>
            <label className="new-order-field optional">
              <span>Rechnungsnummer</span>
              <input
                id="newOrderInvoiceNo"
                type="text"
                autoComplete="off"
                placeholder="optional"
                value={rechnungsnummer}
                onChange={(event) => setRechnungsnummer(event.target.value)}
              />
            </label>
          </div>
          <div className="new-order-billing">
            <div className="new-order-billing-head">
              <strong>Rechnungsadresse</strong>
              <span>Fest hinterlegt</span>
            </div>
            <div className="new-order-address">
              <strong>{ABSINA_BILLING_ADDRESS.company}</strong>
              <span>{ABSINA_BILLING_ADDRESS.companyAddition}</span>
              <span>{ABSINA_BILLING_ADDRESS.street}</span>
              <span>
                {ABSINA_BILLING_ADDRESS.postalCode} {ABSINA_BILLING_ADDRESS.city}
              </span>
              <span>{ABSINA_BILLING_ADDRESS.country}</span>
            </div>
          </div>
          <PositionsEditor
            artikelSuche={positionsArtikelSuche}
            artikelSucheRoh={positionsArtikelSucheRoh}
            positionen={positionen}
            onAendern={setPositionen}
            waehrung={waehrung}
            onExcelDatei={(datei, entwurf) => {
              setExcelDatei(datei);
              setExcelEntwurf(entwurf);
            }}
            onExcelLadenAendern={setExcelBusy}
          />
          <div className="new-order-error" role="alert">
            {fehler}
          </div>
        </div>
        <div className="board-editor-foot">
          <button className="btn-sec" type="button" onClick={onSchliessen} disabled={speichernLaeuft}>
            <Ikone name="x" />
            Abbrechen
          </button>
          <button className="btn-primary" type="submit" disabled={!darfAnlegen || speichernLaeuft}>
            <Ikone name="save" />
            Bestellung speichern
          </button>
        </div>
      </form>
    </section>
  );
}
